<template>
  <div class="task-square-shell">
    <section class="task-square-panel" v-loading="loading">
      <header class="task-square-hero">
        <div>
          <p class="eyebrow">任务广场</p>
          <h1>{{ pageTitle }}</h1>
          <p class="page-subtitle">管理员把需求提交结果发布成可协作任务，普通用户领取后会自动生成自己的项目副本并继续往下推进。</p>
        </div>
        <div class="hero-actions">
          <el-button v-if="userStore.isAdmin" type="primary" plain @click="router.push('/submit')">创建需求</el-button>
          <el-button plain @click="loadTaskSquare">刷新任务</el-button>
          <el-button @click="router.push('/dashboard')">项目工作台</el-button>
        </div>
      </header>

      <div class="stats-grid">
        <article class="stat-card">
          <span>任务总数</span>
          <strong>{{ stats.total }}</strong>
        </article>
        <article class="stat-card stat-card--accent">
          <span>可领取</span>
          <strong>{{ stats.open }}</strong>
        </article>
        <article class="stat-card stat-card--soft">
          <span>{{ userStore.isAdmin ? '总领取人次' : '我的领取' }}</span>
          <strong>{{ userStore.isAdmin ? stats.claims : stats.mine }}</strong>
        </article>
      </div>

      <section v-if="myClaimTasks.length" class="section-block">
        <div class="section-head">
          <div>
            <span class="section-label">我的任务</span>
            <h2>我已领取的任务</h2>
          </div>
        </div>

        <div class="task-grid">
          <article v-for="task in myClaimTasks" :key="`my-${task.id}`" class="task-card task-card--claimed">
            <div class="task-card__header">
              <div>
                <strong>{{ task.title }}</strong>
                <p>{{ task.summary || task.description || '暂无任务摘要' }}</p>
              </div>
              <el-tag type="success">已领取</el-tag>
            </div>

            <div class="task-card__meta">
              <span>来源项目：{{ task.source_project?.title || `项目 ${task.source_project_id}` }}</span>
              <span>当前状态：{{ formatAssignmentStatus(task.viewer_assignment?.status) }}</span>
              <span>当前项目：{{ task.viewer_assignment?.claimed_project?.title || '已创建项目副本' }}</span>
            </div>

            <el-progress :percentage="task.viewer_assignment?.progress_percent || 0" :show-text="false" :stroke-width="10" />

            <div class="task-card__actions">
              <el-button type="primary" @click="openClaimedProject(task)">进入我的项目</el-button>
            </div>
          </article>
        </div>
      </section>

      <section class="section-block">
        <div class="section-head">
          <div>
            <span class="section-label">{{ userStore.isAdmin ? '任务总览' : '可领取任务' }}</span>
            <h2>{{ userStore.isAdmin ? '查看所有协作任务与领取进度' : '选择一个任务开始执行' }}</h2>
          </div>
        </div>

        <div v-if="visibleTasks.length" class="task-grid">
          <article v-for="task in visibleTasks" :key="task.id" class="task-card">
            <div class="task-card__header">
              <div>
                <strong>{{ task.title }}</strong>
                <p>{{ task.summary || task.description || '暂无任务摘要' }}</p>
              </div>
              <div class="task-card__status">
                <el-tag :type="formatTaskStatusType(task.status)">{{ formatTaskStatus(task.status) }}</el-tag>
                <span>{{ task.claim_count }} 人领取</span>
              </div>
            </div>

            <div class="task-card__meta">
              <span>发布人：{{ task.publisher?.username || `用户 ${task.publisher_id}` }}</span>
              <span>来源项目：{{ task.source_project?.title || `项目 ${task.source_project_id}` }}</span>
              <span>项目类型：{{ task.genre || '未设置' }}</span>
            </div>

            <div v-if="userStore.isAdmin && task.assignments.length" class="assignment-list">
              <article v-for="assignment in task.assignments" :key="assignment.id" class="assignment-card">
                <div>
                  <strong>{{ assignment.assignee?.username || `用户 ${assignment.assignee_id}` }}</strong>
                  <p>{{ assignment.claimed_project?.title || `项目 ${assignment.claimed_project_id}` }}</p>
                </div>
                <div class="assignment-card__meta">
                  <el-tag size="small" :type="formatAssignmentStatusType(assignment.status)">{{ formatAssignmentStatus(assignment.status) }}</el-tag>
                  <span>{{ assignment.progress_percent }}%</span>
                  <el-button link type="primary" @click="router.push(buildGenerationRoute(assignment.claimed_project_id))">查看进度</el-button>
                </div>
              </article>
            </div>

            <div class="task-card__actions">
              <template v-if="task.viewer_assignment">
                <el-button type="primary" @click="openClaimedProject(task)">进入我的项目</el-button>
              </template>
              <template v-else-if="!userStore.isAdmin">
                <el-button type="primary" :loading="claimingTaskId === task.id" :disabled="task.status !== 'open'" @click="handleClaimTask(task)">
                  领取并继续执行
                </el-button>
              </template>
              <template v-else>
                <el-button plain @click="router.push(buildGenerationRoute(task.source_project_id))">查看来源项目</el-button>
                <el-button v-if="task.status !== 'open'" type="primary" plain @click="handleUpdateTaskStatus(task, 'open')">重新开放</el-button>
                <el-button v-else type="warning" plain @click="handleUpdateTaskStatus(task, 'paused')">暂停领取</el-button>
                <el-button v-if="task.status !== 'closed'" type="danger" plain @click="handleUpdateTaskStatus(task, 'closed')">关闭任务</el-button>
              </template>
            </div>
          </article>
        </div>

        <el-empty v-else description="当前还没有任务，可先在需求提交页创建并发布任务。" />
      </section>
    </section>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import { useRouter } from 'vue-router'
import { ElMessage } from 'element-plus'

import { isAuthRedirectError } from '@/api/request'
import { taskSquareApi, type TaskSquareStatus, type TaskSquareTask } from '@/api/taskSquare'
import { useProjectStore } from '@/stores/project'
import { useUserStore } from '@/stores/user'
import type { StartWorkflowRequest } from '@/types/workflow'
import { buildGenerationRoute, queueGenerationAction } from '@/utils/workflowGeneration'

const router = useRouter()
const userStore = useUserStore()
const projectStore = useProjectStore()

const loading = ref(false)
const claimingTaskId = ref<number | null>(null)
const tasks = ref<TaskSquareTask[]>([])

const pageTitle = computed(() => userStore.isAdmin ? '管理员任务广场' : '协作任务广场')
const myClaimTasks = computed(() => tasks.value.filter((item) => Boolean(item.viewer_assignment)))
const visibleTasks = computed(() => userStore.isAdmin ? tasks.value : tasks.value.filter((item) => !item.viewer_assignment))

const stats = computed(() => ({
  total: tasks.value.length,
  open: tasks.value.filter((item) => item.status === 'open').length,
  claims: tasks.value.reduce((sum, item) => sum + item.claim_count, 0),
  mine: myClaimTasks.value.length,
}))

function formatTaskStatus(status: TaskSquareStatus) {
  const labels: Record<TaskSquareStatus, string> = {
    open: '可领取',
    paused: '暂停中',
    closed: '已关闭',
  }
  return labels[status] || status
}

function formatTaskStatusType(status: TaskSquareStatus) {
  if (status === 'open') return 'success'
  if (status === 'paused') return 'warning'
  return 'info'
}

function formatAssignmentStatus(status?: string | null) {
  const labels: Record<string, string> = {
    claimed: '已领取',
    in_progress: '执行中',
    reviewing: '审核中',
    completed: '已完成',
    failed: '已失败',
  }
  return status ? labels[status] || status : '未开始'
}

function formatAssignmentStatusType(status?: string | null) {
  if (status === 'completed') return 'success'
  if (status === 'reviewing') return 'warning'
  if (status === 'failed') return 'danger'
  return 'primary'
}

async function loadTaskSquare() {
  loading.value = true
  try {
    const taskResponse = await taskSquareApi.list({ suppressErrorMessage: true })

    tasks.value = (taskResponse.items || []).map((item) => ({
      ...item,
      assignments: Array.isArray(item.assignments) ? item.assignments : [],
      viewer_assignment: item.viewer_assignment || null,
    }))
  } catch (error) {
    if (isAuthRedirectError(error)) {
      return
    }
    ElMessage.error('加载任务广场失败')
  } finally {
    loading.value = false
  }
}

async function handleUpdateTaskStatus(task: TaskSquareTask, status: TaskSquareStatus) {
  try {
    await taskSquareApi.updateStatus(task.id, status)
    ElMessage.success('任务状态已更新')
    await loadTaskSquare()
  } catch {
    ElMessage.error('更新任务状态失败')
  }
}

async function handleClaimTask(task: TaskSquareTask) {
  claimingTaskId.value = task.id
  try {
    const response = await taskSquareApi.claim(task.id)
    projectStore.addProject(response.claimed_project)
    queueGenerationAction(response.claimed_project.id, {
      intent: 'start',
      stage: 'proposal',
      startRequest: response.start_request as StartWorkflowRequest,
    })
    ElMessage.success('任务已领取，正在进入你的项目工作台')
    await router.push(buildGenerationRoute(response.claimed_project.id, { intent: 'start', stage: 'proposal' }))
  } catch {
    ElMessage.error('领取任务失败')
  } finally {
    claimingTaskId.value = null
  }
}

function openClaimedProject(task: TaskSquareTask) {
  if (!task.viewer_assignment?.claimed_project_id) {
    return
  }
  void router.push(buildGenerationRoute(task.viewer_assignment.claimed_project_id))
}

onMounted(() => {
  void loadTaskSquare()
})
</script>

<style scoped>
.task-square-shell {
  min-height: 100vh;
  padding: 28px;
  background:
    radial-gradient(circle at top left, rgba(234, 179, 8, 0.18), transparent 24%),
    radial-gradient(circle at bottom right, rgba(14, 165, 233, 0.12), transparent 28%),
    linear-gradient(180deg, #f7fafc 0%, #edf3f8 100%);
}

.task-square-panel {
  max-width: 1380px;
  margin: 0 auto;
  display: grid;
  gap: 20px;
}

.task-square-hero,
.section-block,
.stat-card,
.task-card {
  border-radius: 28px;
  background: rgba(255, 255, 255, 0.94);
  border: 1px solid rgba(148, 163, 184, 0.18);
  box-shadow: 0 24px 60px rgba(15, 23, 42, 0.08);
}

.task-square-hero,
.section-block {
  padding: 28px;
}

.task-square-hero,
.section-head,
.hero-actions,
.task-card__header,
.task-card__status,
.task-card__actions,
.assignment-card,
.assignment-card__meta {
  display: flex;
  justify-content: space-between;
  gap: 16px;
  align-items: flex-start;
}

.eyebrow,
.section-label {
  display: block;
  color: #9a3412;
  font-size: 12px;
  font-weight: 800;
  letter-spacing: 0.12em;
  text-transform: uppercase;
}

.task-square-hero h1,
.section-head h2,
.task-card strong,
.assignment-card strong {
  color: #0f172a;
}

.page-subtitle,
.section-head p,
.task-card p,
.assignment-card p {
  margin-top: 8px;
  color: #64748b;
  line-height: 1.7;
}

.stats-grid,
.task-grid {
  display: grid;
  gap: 16px;
}

.stats-grid {
  grid-template-columns: repeat(3, minmax(0, 1fr));
}

.stat-card {
  padding: 22px;
  display: grid;
  gap: 8px;
}

.stat-card strong {
  font-size: 34px;
  color: #0f172a;
}

.stat-card span {
  color: #64748b;
}

.stat-card--accent {
  background: linear-gradient(180deg, rgba(236, 253, 245, 0.96), rgba(255, 255, 255, 0.96));
}

.stat-card--soft {
  background: linear-gradient(180deg, rgba(239, 246, 255, 0.96), rgba(255, 255, 255, 0.96));
}

.task-grid {
  grid-template-columns: repeat(2, minmax(0, 1fr));
}

.task-card {
  padding: 24px;
  display: grid;
  gap: 16px;
}

.task-card--claimed {
  background: linear-gradient(180deg, rgba(236, 253, 245, 0.82), rgba(255, 255, 255, 0.96));
}

.task-card__meta {
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
  color: #64748b;
  font-size: 13px;
}

.assignment-list {
  display: grid;
  gap: 12px;
}

.assignment-card {
  padding: 14px 16px;
  border-radius: 18px;
  background: rgba(248, 250, 252, 0.92);
  border: 1px solid rgba(148, 163, 184, 0.16);
}

@media (max-width: 1080px) {
  .stats-grid,
  .task-grid {
    grid-template-columns: 1fr;
  }

  .task-square-hero,
  .section-head,
  .hero-actions,
  .task-card__header,
  .task-card__actions,
  .assignment-card,
  .assignment-card__meta {
    flex-direction: column;
  }
}
</style>